const maxStringLength = 8000;
const sensitiveKeyPattern = /(password|token|secret|authorization|cookie|email|phone|address)/i;

function truncateString(value) {
  if (value.length <= maxStringLength) {
    return value;
  }

  return value.slice(0, maxStringLength) + '...[truncated]';
}

export function sanitizeValue(value, depth = 0) {
  if (depth > 4) {
    return '[max-depth]';
  }

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (Array.isArray(value)) {
    return value.slice(0, 30).map((item) => sanitizeValue(item, depth + 1));
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).slice(0, 60).reduce((result, key) => {
      if (sensitiveKeyPattern.test(key)) {
        result[key] = '[redacted]';
        return result;
      }

      try {
        result[key] = sanitizeValue(value[key], depth + 1);
      } catch (error) {
        result[key] = '[unreadable]';
      }

      return result;
    }, {});
  }

  return value;
}

function getSafeKvConfig() {
  const kvConfig = document.kvConfig || {};
  const safeKeys = [
    'userId',
    'jobId',
    'visualizerType',
    'navigationType',
    'toolVersion',
    'subDomain',
    'domain',
    'kioskMode',
    'noInternetMode',
    'offlineMode',
    'isBackend',
    'devMode',
    'visualizerOnly',
    'layersSize',
    'useLocalComposer',
  ];

  return safeKeys.reduce((result, key) => {
    if (kvConfig[key] !== undefined) {
      // userId is an identifier, not a quantity - keep it a string everywhere
      // so log viewers don't apply numeric formatting (thousands separators) to it.
      result[key] = key === 'userId' ? String(kvConfig[key]) : sanitizeValue(kvConfig[key]);
    }

    return result;
  }, {});
}

function getBrowserContext() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    timezone: typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
    online: navigator.onLine,
  };
}

export function errorToPlainObject(error) {
  if (!error) {
    return {};
  }

  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  return {
    name: error.name,
    message: error.message || String(error),
    stack: error.stack,
    code: error.code,
    status: error.response && error.response.status || error.status,
  };
}

// Keep triage fields before browser/kvConfig context for readable raw reports.
export function buildErrorPayload({type, errorData, extra}) {
  const kvConfig = getSafeKvConfig();
  const browser = getBrowserContext();
  const url = window.location.href;
  const message = errorData.message || 'Unknown frontend error';

  return sanitizeValue({
    type,
    name: errorData.name,
    message,
    code: errorData.code,
    status: errorData.status,
    url,
    stack: errorData.stack,
    reactStack: extra && extra.reactStack,
    source: errorData.source,
    line: errorData.line,
    column: errorData.column,
    level: 'fatal',
    app: 'visualizer',
    timestamp: new Date().toISOString(),
    browser,
    kvConfig,
  });
}
